import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Plus, Spline } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { isDrawableLine } from '../../../components/climbs/route-line';
import type { CragRoute } from '../../../types/crag-route';

/** ⌘/Ctrl and Shift, as read off the click that picked a row. */
export interface RowClickModifiers {
  additive: boolean;
  range: boolean;
}

interface RouteListColumnProps {
  /** The open sector's routes, in the order they are walked past. */
  routes: CragRoute[];
  /** The crag's routes that are in no sector at all. */
  unassigned: CragRoute[];
  /** The route the editor has open. */
  selectedId: string | null;
  /** Every row picked out — what a drag onto a sector would carry. */
  pickedIds: string[];
  onRowClick: (routeId: string, modifiers: RowClickModifiers) => void;
  onClearSelection: () => void;
  onAdd: () => void;
  loading?: boolean;
  /** No sector chosen yet — there is nothing to list and nothing to add to. */
  disabled?: boolean;
}

/**
 * The sector's routes, in the order they are walked past — which is the order
 * the crag's public page lists them in, so this column *is* the topo.
 *
 * Dragging is how that order is set, and also how a route changes sector:
 * dropped on another route it moves up or down the topo, dropped on a cliff in
 * the column to the left it moves to that cliff. The handle is its own control
 * rather than the whole card: the card is a button that opens the route, and a
 * card that both opens on press and moves on drag makes each gesture
 * unreliable.
 *
 * Above the topo sit the routes in no sector at all — members adding one from
 * the crag page are never asked for one. They are nobody's topo, so they cannot
 * be reordered and there is no photo to draw them on until they are filed;
 * what they are here for is to be dragged onto a cliff, a handful at a time.
 */
export function RouteListColumn({
  routes,
  unassigned,
  selectedId,
  pickedIds,
  onRowClick,
  onClearSelection,
  onAdd,
  loading,
  disabled,
}: RouteListColumnProps) {
  const { t } = useTranslation();

  if (disabled) {
    return <p className="p-3 text-sm text-muted-foreground">{t('cragAdmin.routes.selectSector')}</p>;
  }

  if (loading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  // `select-none`: Shift-clicking down a list otherwise paints it blue, which
  // reads as the page having gone wrong rather than as a selection.
  return (
    <div className="select-none space-y-2 p-2">
      {pickedIds.length > 1 && (
        <div className="space-y-0.5 rounded-md border border-primary bg-accent px-2 py-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium">
              {t('cragAdmin.routes.selectedCount', { count: pickedIds.length })}
            </span>
            <button type="button" onClick={onClearSelection} className="text-xs underline">
              {t('cragAdmin.routes.clearSelection')}
            </button>
          </div>
          {/* Dragging one of them carries the lot, which is not something a
              selection says on its own — so it is said here. */}
          <p className="text-[11px] leading-tight text-muted-foreground">
            {t('cragAdmin.routes.selectedHint')}
          </p>
        </div>
      )}

      {unassigned.length > 0 && (
        <section
          className="space-y-1 rounded-md border border-dashed bg-muted/40 p-2"
          aria-label={t('cragAdmin.routes.unassignedTitle')}
          data-testid="crag-unassigned-routes"
        >
          <header>
            <p className="text-xs font-medium">
              {t('cragAdmin.routes.unassignedTitle')} · {unassigned.length}
            </p>
            <p className="text-[11px] leading-tight text-muted-foreground">
              {t('cragAdmin.routes.unassignedHint')}
            </p>
          </header>
          {/* Bounded: an import can leave thirty routes unfiled, and the topo
              below is still the column's job. */}
          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {unassigned.map((route) => (
              <UnfiledRouteRow
                key={route.id}
                route={route}
                picked={pickedIds.includes(route.id)}
                onRowClick={onRowClick}
              />
            ))}
          </ul>
        </section>
      )}

      {routes.length === 0 && (
        <p className="p-2 text-sm text-muted-foreground">{t('cragAdmin.routes.noRoutes')}</p>
      )}

      <SortableContext items={routes.map((route) => route.id)} strategy={verticalListSortingStrategy}>
        <ul className="space-y-1">
          {routes.map((route, index) => (
            <SortableRouteRow
              key={route.id}
              route={route}
              index={index}
              open={route.id === selectedId}
              picked={pickedIds.includes(route.id)}
              onRowClick={onRowClick}
            />
          ))}
        </ul>
      </SortableContext>

      {/* At the foot of the list, where a new line joins the topo. */}
      <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" onClick={onAdd}>
        <Plus className="size-4" aria-hidden />
        {t('cragAdmin.routes.add')}
      </Button>
    </div>
  );
}

/**
 * The card under the pointer while a drag is on, rendered by the page in a
 * `DragOverlay`.
 *
 * It has to be an overlay rather than the row itself moving: the unfiled routes
 * sit in a box that scrolls, and a card dragged out of a scrolling box is
 * clipped by it. The overlay is also where the count of what is coming along
 * belongs — a selection of six leaves six rows behind, and only the thing being
 * carried can say how many it is.
 */
export function RouteDragPreview({ route, count }: { route: CragRoute; count: number }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-primary bg-card px-2 py-2 shadow-lg">
      <GripVertical className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{route.name}</span>
      {count > 1 && (
        <span className="shrink-0 rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
          {count}
        </span>
      )}
    </div>
  );
}

function SortableRouteRow({
  route,
  index,
  open,
  picked,
  onRowClick,
}: {
  route: CragRoute;
  index: number;
  open: boolean;
  picked: boolean;
  onRowClick: (routeId: string, modifiers: RowClickModifiers) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: route.id,
  });

  return (
    <RouteRow
      route={route}
      index={index}
      open={open}
      picked={picked}
      dragging={isDragging}
      setNodeRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      dragLabel={t('cragAdmin.routes.reorderHandle', { name: route.name })}
      handleProps={{ ...attributes, ...listeners }}
      onRowClick={onRowClick}
    />
  );
}

/**
 * A route in no sector. Draggable but not sortable: it has no place in any topo
 * to be moved up or down, and the only thing it is dragged for is to be given
 * one.
 */
function UnfiledRouteRow({
  route,
  picked,
  onRowClick,
}: {
  route: CragRoute;
  picked: boolean;
  onRowClick: (routeId: string, modifiers: RowClickModifiers) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: route.id });

  return (
    <RouteRow
      route={route}
      index={null}
      open={false}
      picked={picked}
      dragging={isDragging}
      setNodeRef={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      dragLabel={t('cragAdmin.routes.moveHandle', { name: route.name })}
      handleProps={{ ...attributes, ...listeners }}
      onRowClick={onRowClick}
      // The only photo an unfiled route has is its own — whoever added it from
      // the crag page shot it from the ground, and it is what says which cliff
      // this is, which is the whole question being answered here.
      thumbnail={
        route.image_url ? (
          <img
            src={route.image_url}
            alt=""
            className="size-8 shrink-0 rounded object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : null
      }
    />
  );
}

function RouteRow({
  route,
  index,
  open,
  picked,
  dragging,
  setNodeRef,
  style,
  dragLabel,
  handleProps,
  onRowClick,
  thumbnail,
}: {
  route: CragRoute;
  /** Its number in the topo. Null for a route that is in no sector. */
  index: number | null;
  open: boolean;
  picked: boolean;
  dragging: boolean;
  setNodeRef: (node: HTMLElement | null) => void;
  style: CSSProperties;
  dragLabel: string;
  handleProps: Record<string, unknown>;
  onRowClick: (routeId: string, modifiers: RowClickModifiers) => void;
  thumbnail?: ReactNode;
}) {
  const { t } = useTranslation();

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onRowClick(route.id, {
      // ⌘ on a Mac, Ctrl everywhere else; both are taken everywhere, because
      // the panel is used on whatever the gym has.
      additive: event.metaKey || event.ctrlKey,
      range: event.shiftKey,
    });
  }

  return (
    <li
      ref={setNodeRef}
      style={{ ...style, opacity: dragging ? 0.5 : 1 }}
      data-testid="crag-route-row"
      data-picked={picked ? 'true' : undefined}
      className={cn(
        'relative flex items-center gap-1 rounded-md border bg-card transition-colors',
        // Open and picked are two different things and have to look it: the
        // editor shows one route, a drag carries every picked one.
        picked && 'bg-accent ring-1 ring-primary',
        open && 'border-primary bg-accent'
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none p-1.5 text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={dragLabel}
        {...handleProps}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>

      <button
        type="button"
        onClick={handleClick}
        aria-current={open}
        className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-2 text-left"
      >
        {index === null ? thumbnail : (
          <span className="w-5 shrink-0 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{route.name}</span>
        {/* Which lines are already drawn is the thing this column is scanned
            for — it is the difference between a route that is done and one
            still waiting for somebody to trace it. A line traced on another
            photo is not drawn on this cliff, so it does not count as done. */}
        {isDrawableLine(route.line) && route.line_on_sector_photo && (
          <Spline className="size-3.5 shrink-0 text-primary" aria-label={t('cragAdmin.routes.hasLine')} />
        )}
        {route.difficulty && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
            {route.difficulty}
          </span>
        )}
      </button>

    </li>
  );
}
